import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";

import {
  Container,
  Segment,
  Table,
  Grid,
  Header,
  Icon,
  Label,
  Button,
  Message,
  Input,
  Dropdown,
} from "semantic-ui-react";

import moment from "moment";

import { UserContext } from "../context/UserContext";
import { db } from "../firebase/firebaseConfig";

import {
  getDocs,
  collection,
  where,
  query,
} from "firebase/firestore";

const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#253858",
  whiteSpace: "nowrap",
};

const ESTADOS = {
  ALL: "ALL",
  PENDIENTE: "PENDIENTE",
  PROXIMA: "PROXIMA",
  VENCIDA: "VENCIDA",
};

const ListadoMant = () => {
  const { users } = useContext(UserContext);

  const [mantencion, setMantencion] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  // ============================================================
  // FILTROS
  // ============================================================

  const [busqueda, setBusqueda] =
    useState("");

  const [
    estadoFiltro,
    setEstadoFiltro,
  ] = useState(ESTADOS.ALL);

  // ============================================================
  // CARGAR MANTENCIONES
  // ============================================================

  const getMantenimiento =
    useCallback(async () => {
      if (!users?.emp_id) {
        setMantencion([]);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const mantRef = collection(
          db,
          "mantenciones"
        );

        const qMant = query(
          mantRef,
          where(
            "emp_id",
            "==",
            users.emp_id
          )
        );

        const snap =
          await getDocs(qMant);

        const datos =
          snap.docs.map((documento) => ({
            ...documento.data(),
            id: documento.id,
          }));

        setMantencion(datos);
      } catch (error) {
        console.error(
          "Error al obtener mantenciones:",
          error
        );

        setMantencion([]);

        setError(
          "No fue posible cargar las mantenciones."
        );
      } finally {
        setLoading(false);
      }
    }, [users?.emp_id]);

  useEffect(() => {
    getMantenimiento();
  }, [getMantenimiento]);

  // ============================================================
  // FORMATEAR FECHA
  // ============================================================

  const formatearFecha =
    useCallback((fecha) => {
      if (!fecha?.toDate) {
        return "-";
      }

      return moment(
        fecha.toDate()
      ).format("DD/MM/YYYY");
    }, []);

  // ============================================================
  // DÍAS RESTANTES
  //
  // Se utiliza solamente para PRESENTACIÓN.
  //
  // El estado oficial sigue siendo:
  // mantenciones.estado
  //
  // Esta fórmula replica la lógica utilizada por Cloud Run:
  //
  // Math.ceil(
  //   (fechaTermino - ahora) /
  //   (1000 * 60 * 60 * 24)
  // )
  // ============================================================

  const calcularDiasRestantes =
    useCallback((fechaTermino) => {
      if (!fechaTermino?.toDate) {
        return null;
      }

      const fecha =
        fechaTermino.toDate();

      const ahora =
        new Date();

      const diffMs =
        fecha.getTime() -
        ahora.getTime();

      return Math.ceil(
        diffMs /
          (1000 *
            60 *
            60 *
            24)
      );
    }, []);

  // ============================================================
  // ESTADO OFICIAL
  //
  // IMPORTANTE:
  //
  // NO recalculamos:
  // PENDIENTE / PROXIMA / VENCIDA
  //
  // Esa responsabilidad queda en Cloud Run.
  // ============================================================

  const obtenerEstadoOficial =
    useCallback((item) => {
      const estado =
        String(
          item?.estado ||
            ESTADOS.PENDIENTE
        )
          .trim()
          .toUpperCase();

      if (
        estado ===
        ESTADOS.VENCIDA
      ) {
        return ESTADOS.VENCIDA;
      }

      if (
        estado ===
        ESTADOS.PROXIMA
      ) {
        return ESTADOS.PROXIMA;
      }

      if (
        estado ===
        ESTADOS.PENDIENTE
      ) {
        return ESTADOS.PENDIENTE;
      }

      return estado;
    }, []);

  // ============================================================
  // PRESENTACIÓN DEL ESTADO
  //
  // "VENCE HOY" no es un nuevo estado de Firestore.
  //
  // Sigue siendo:
  // estado = PROXIMA
  //
  // Solamente cambia el texto mostrado al usuario.
  // ============================================================

  const obtenerEstadoVisual =
    useCallback(
      (item) => {
        const estado =
          obtenerEstadoOficial(
            item
          );

        const dias =
          calcularDiasRestantes(
            item?.fecha_termino
          );

        if (
          estado ===
          ESTADOS.VENCIDA
        ) {
          const diasVencida =
            dias !== null &&
            dias < 0
              ? Math.abs(dias)
              : null;

          return {
            estado,
            label:
              diasVencida !== null
                ? `VENCIDA · ${diasVencida}d`
                : "VENCIDA",
            color: "red",
            icon: "warning circle",
            dias,
          };
        }

        if (
          estado ===
          ESTADOS.PROXIMA
        ) {
          if (dias === 0) {
            return {
              estado,
              label: "VENCE HOY",
              color: "orange",
              icon:
                "clock outline",
              dias,
            };
          }

          if (
            dias !== null &&
            dias > 0
          ) {
            return {
              estado,
              label: `PRÓXIMA · ${dias}d`,
              color: "orange",
              icon:
                "clock outline",
              dias,
            };
          }

          return {
            estado,
            label: "PRÓXIMA",
            color: "orange",
            icon:
              "clock outline",
            dias,
          };
        }

        if (
          estado ===
          ESTADOS.PENDIENTE
        ) {
          return {
            estado,
            label:
              dias !== null &&
              dias > 0
                ? `PENDIENTE · ${dias}d`
                : "PENDIENTE",
            color: "blue",
            icon:
              "calendar outline",
            dias,
          };
        }

        return {
          estado,
          label:
            estado ||
            "SIN ESTADO",
          color: "grey",
          icon:
            "question circle outline",
          dias,
        };
      },
      [
        obtenerEstadoOficial,
        calcularDiasRestantes,
      ]
    );

  // ============================================================
  // ORDEN
  //
  // Más próxima primero.
  // ============================================================

  const manteOrd =
    useMemo(() => {
      return [
        ...mantencion,
      ].sort((a, b) => {
        const aMs =
          a?.fecha_termino
            ?.toMillis?.() ?? 0;

        const bMs =
          b?.fecha_termino
            ?.toMillis?.() ?? 0;

        return aMs - bMs;
      });
    }, [mantencion]);

  // ============================================================
  // FILTROS
  // ============================================================

  const manteFiltrada =
    useMemo(() => {
      const textoBusqueda =
        (busqueda || "")
          .trim()
          .toLowerCase();

      const porEstado =
        manteOrd.filter(
          (item) => {
            if (
              estadoFiltro ===
              ESTADOS.ALL
            ) {
              return true;
            }

            return (
              obtenerEstadoOficial(
                item
              ) ===
              estadoFiltro
            );
          }
        );

      if (!textoBusqueda) {
        return porEstado;
      }

      return porEstado.filter(
        (item) => {
          const contenido = [
            item.familia,
            item.tipo,
            item.serie,
            item.nombre_protocolo,
            item.programa,
            item.id_eq,
            item.cab_id_protocol,
            item.estado,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return contenido.includes(
            textoBusqueda
          );
        }
      );
    }, [
      manteOrd,
      busqueda,
      estadoFiltro,
      obtenerEstadoOficial,
    ]);

  // ============================================================
  // KPIs
  //
  // Se calculan utilizando exclusivamente el estado
  // almacenado en Firestore.
  // ============================================================

  const total =
    manteOrd.length;

  const pendientes =
    useMemo(
      () =>
        manteOrd.filter(
          (item) =>
            obtenerEstadoOficial(
              item
            ) ===
            ESTADOS.PENDIENTE
        ).length,
      [
        manteOrd,
        obtenerEstadoOficial,
      ]
    );

  const proximas =
    useMemo(
      () =>
        manteOrd.filter(
          (item) =>
            obtenerEstadoOficial(
              item
            ) ===
            ESTADOS.PROXIMA
        ).length,
      [
        manteOrd,
        obtenerEstadoOficial,
      ]
    );

  const vencidas =
    useMemo(
      () =>
        manteOrd.filter(
          (item) =>
            obtenerEstadoOficial(
              item
            ) ===
            ESTADOS.VENCIDA
        ).length,
      [
        manteOrd,
        obtenerEstadoOficial,
      ]
    );

  // ============================================================
  // OPCIONES FILTRO
  // ============================================================

  const opcionesEstado =
    useMemo(
      () => [
        {
          key: ESTADOS.ALL,
          text:
            "Todas las mantenciones",
          value: ESTADOS.ALL,
          icon: "list",
        },
        {
          key:
            ESTADOS.PENDIENTE,
          text: "Pendientes",
          value:
            ESTADOS.PENDIENTE,
          icon:
            "calendar outline",
        },
        {
          key:
            ESTADOS.PROXIMA,
          text: "Próximas",
          value:
            ESTADOS.PROXIMA,
          icon:
            "clock outline",
        },
        {
          key:
            ESTADOS.VENCIDA,
          text: "Vencidas",
          value:
            ESTADOS.VENCIDA,
          icon:
            "warning circle",
        },
      ],
      []
    );

  // ============================================================
  // LIMPIAR FILTROS
  // ============================================================

  const limpiarFiltros =
    useCallback(() => {
      setBusqueda("");
      setEstadoFiltro(
        ESTADOS.ALL
      );
    }, []);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "24px 16px 40px",
      }}
    >
      <Container
        fluid
        style={{
          maxWidth: 1400,
          margin: "0 auto",
        }}
      >
        {/* =====================================================
            CABECERA
        ===================================================== */}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems:
                "center",
              gap: 14,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 8,
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                background:
                  "#e7f7f8",
                color:
                  "#0d9da5",
              }}
            >
              <Icon
                name="calendar check outline"
                size="large"
                style={{
                  margin: 0,
                }}
              />
            </div>

            <div>
              <Header
                as="h2"
                style={{
                  margin: 0,
                  color:
                    "#172b4d",
                }}
              >
                Mantenciones
                Programadas
              </Header>

              <div
                style={{
                  marginTop: 4,
                  color:
                    "#6b778c",
                  fontSize: 14,
                }}
              >
                Seguimiento de
                programas y
                vencimientos de
                mantención
                preventiva.
              </div>
            </div>
          </div>

          <Button
            basic
            icon
            labelPosition="left"
            onClick={
              getMantenimiento
            }
            loading={loading}
            disabled={loading}
          >
            <Icon name="refresh" />
            Actualizar
          </Button>
        </div>

        {/* =====================================================
            ERROR
        ===================================================== */}

        {error && (
          <Message
            negative
            icon
          >
            <Icon
              name="warning circle"
            />

            <Message.Content>
              <Message.Header>
                Error al cargar
              </Message.Header>

              {error}
            </Message.Content>
          </Message>
        )}

        {/* =====================================================
            KPIs
        ===================================================== */}

        <Grid
          stackable
          columns={4}
          style={{
            marginBottom: 8,
          }}
        >
          <Grid.Column>
            <Segment
              style={{
                border:
                  "1px solid #e5e9ef",
                boxShadow:
                  "0 1px 3px rgba(9,30,66,0.08)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                }}
              >
                <div>
                  <div
                    style={{
                      color:
                        "#6b778c",
                      fontSize: 13,
                      fontWeight:
                        600,
                    }}
                  >
                    TOTAL
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 26,
                      fontWeight:
                        700,
                      color:
                        "#172b4d",
                    }}
                  >
                    {total}
                  </div>
                </div>

                <Icon
                  name="list alternate outline"
                  size="large"
                  style={{
                    color:
                      "#0d9da5",
                  }}
                />
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment
              style={{
                border:
                  "1px solid #e5e9ef",
                boxShadow:
                  "0 1px 3px rgba(9,30,66,0.08)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                }}
              >
                <div>
                  <div
                    style={{
                      color:
                        "#6b778c",
                      fontSize: 13,
                      fontWeight:
                        600,
                    }}
                  >
                    PENDIENTES
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 26,
                      fontWeight:
                        700,
                      color:
                        "#2185d0",
                    }}
                  >
                    {pendientes}
                  </div>
                </div>

                <Icon
                  name="calendar outline"
                  size="large"
                  style={{
                    color:
                      "#2185d0",
                  }}
                />
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment
              style={{
                border:
                  "1px solid #e5e9ef",
                boxShadow:
                  "0 1px 3px rgba(9,30,66,0.08)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                }}
              >
                <div>
                  <div
                    style={{
                      color:
                        "#6b778c",
                      fontSize: 13,
                      fontWeight:
                        600,
                    }}
                  >
                    PRÓXIMAS
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 26,
                      fontWeight:
                        700,
                      color:
                        "#f2711c",
                    }}
                  >
                    {proximas}
                  </div>
                </div>

                <Icon
                  name="clock outline"
                  size="large"
                  style={{
                    color:
                      "#f2711c",
                  }}
                />
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Segment
              style={{
                border:
                  "1px solid #e5e9ef",
                boxShadow:
                  "0 1px 3px rgba(9,30,66,0.08)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                }}
              >
                <div>
                  <div
                    style={{
                      color:
                        "#6b778c",
                      fontSize: 13,
                      fontWeight:
                        600,
                    }}
                  >
                    VENCIDAS
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 26,
                      fontWeight:
                        700,
                      color:
                        "#db2828",
                    }}
                  >
                    {vencidas}
                  </div>
                </div>

                <Icon
                  name="warning circle"
                  size="large"
                  style={{
                    color:
                      "#db2828",
                  }}
                />
              </div>
            </Segment>
          </Grid.Column>
        </Grid>

        {/* =====================================================
            LISTADO
        ===================================================== */}

        <Segment
          style={{
            marginTop: 14,
            padding: 0,
            border:
              "1px solid #e5e9ef",
            boxShadow:
              "0 1px 3px rgba(9,30,66,0.08)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {/* CABECERA TARJETA */}

          <div
            style={{
              padding:
                "14px 18px",
              borderBottom:
                "1px solid #e5e9ef",
              display: "flex",
              alignItems:
                "center",
              justifyContent:
                "space-between",
              gap: 16,
              flexWrap: "wrap",
              background:
                "#ffffff",
            }}
          >
            <div
              style={{
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 8,
                color:
                  "#253858",
                fontWeight: 700,
              }}
            >
              <Icon
                name="tasks"
                style={{
                  color:
                    "#0d9da5",
                }}
              />

              Plan de
              mantenciones
            </div>

            <div
              style={{
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 10,
                flexWrap:
                  "wrap",
              }}
            >
              <Input
                icon="search"
                placeholder="Buscar serie, equipo, familia, programa..."
                value={busqueda}
                onChange={(e) =>
                  setBusqueda(
                    e.target
                      .value
                  )
                }
                style={{
                  minWidth: 320,
                }}
              />

              <Dropdown
                selection
                options={
                  opcionesEstado
                }
                value={
                  estadoFiltro
                }
                onChange={(
                  _,
                  data
                ) =>
                  setEstadoFiltro(
                    data.value
                  )
                }
                style={{
                  minWidth: 190,
                }}
              />

              <Button
                basic
                icon
                title="Limpiar filtros"
                onClick={
                  limpiarFiltros
                }
              >
                <Icon name="eraser" />
              </Button>
            </div>
          </div>

          {/* TABLA */}

          <div
            style={{
              overflowX:
                "auto",
              maxHeight: 580,
              overflowY:
                "auto",
            }}
          >
            <Table
              celled
              compact
              unstackable
              selectable
              style={{
                margin: 0,
                minWidth: 1100,
                border: 0,
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                    width={1}
                  >
                    N°
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Equipo
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Serie
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Protocolo
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                    textAlign="center"
                  >
                    Programa
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Fecha Inicio
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                  >
                    Próx.
                    Mantención
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={
                      stickyTh
                    }
                    textAlign="center"
                  >
                    Estado
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {loading && (
                  <Table.Row>
                    <Table.Cell
                      colSpan={
                        8
                      }
                    >
                      <Message
                        icon
                        info
                        style={{
                          margin:
                            0,
                        }}
                      >
                        <Icon
                          name="spinner"
                          loading
                        />

                        <Message.Content>
                          <Message.Header>
                            Cargando
                            mantenciones
                          </Message.Header>

                          Consultando
                          información
                          de
                          mantenciones.
                        </Message.Content>
                      </Message>
                    </Table.Cell>
                  </Table.Row>
                )}

                {!loading &&
                  manteFiltrada.map(
                    (
                      item,
                      index
                    ) => {
                      const estadoVisual =
                        obtenerEstadoVisual(
                          item
                        );

                      return (
                        <Table.Row
                          key={
                            item.id ||
                            index
                          }
                        >
                          <Table.Cell>
                            {index +
                              1}
                          </Table.Cell>

                          <Table.Cell>
                            <div
                              style={{
                                fontWeight:
                                  600,
                                color:
                                  "#253858",
                              }}
                            >
                              {item.tipo ||
                                "-"}
                            </div>

                            {item.familia && (
                              <div
                                style={{
                                  marginTop:
                                    3,
                                  fontSize:
                                    12,
                                  color:
                                    "#6b778c",
                                }}
                              >
                                {
                                  item.familia
                                }
                              </div>
                            )}
                          </Table.Cell>

                          <Table.Cell>
                            <strong>
                              {item.serie ||
                                "-"}
                            </strong>
                          </Table.Cell>

                          <Table.Cell
                            style={{
                              whiteSpace:
                                "normal",
                            }}
                          >
                            {item.nombre_protocolo ||
                              "-"}
                          </Table.Cell>

                          <Table.Cell
                            textAlign="center"
                          >
                            {item.programa ? (
                              <Label
                                basic
                                color="teal"
                                size="tiny"
                              >
                                {
                                  item.programa
                                }
                              </Label>
                            ) : (
                              "-"
                            )}

                            {item.dias ? (
                              <div
                                style={{
                                  marginTop:
                                    5,
                                  fontSize:
                                    11,
                                  color:
                                    "#6b778c",
                                }}
                              >
                                {
                                  item.dias
                                }{" "}
                                días
                              </div>
                            ) : null}
                          </Table.Cell>

                          <Table.Cell>
                            {formatearFecha(
                              item.fecha_inicio
                            )}
                          </Table.Cell>

                          <Table.Cell>
                            {formatearFecha(
                              item.fecha_termino
                            )}
                          </Table.Cell>

                          <Table.Cell
                            textAlign="center"
                          >
                            <Label
                              color={
                                estadoVisual.color
                              }
                              size="tiny"
                            >
                              <Icon
                                name={
                                  estadoVisual.icon
                                }
                              />

                              {
                                estadoVisual.label
                              }
                            </Label>
                          </Table.Cell>
                        </Table.Row>
                      );
                    }
                  )}

                {!loading &&
                  manteFiltrada.length ===
                    0 && (
                    <Table.Row>
                      <Table.Cell
                        colSpan={
                          8
                        }
                      >
                        <Message
                          icon
                          style={{
                            margin:
                              0,
                          }}
                        >
                          <Icon name="search" />

                          <Message.Content>
                            <Message.Header>
                              Sin
                              resultados
                            </Message.Header>

                            No hay
                            mantenciones
                            que
                            coincidan
                            con los
                            filtros
                            seleccionados.
                          </Message.Content>
                        </Message>
                      </Table.Cell>
                    </Table.Row>
                  )}
              </Table.Body>
            </Table>
          </div>

          {/* PIE */}

          <div
            style={{
              padding:
                "10px 16px",
              borderTop:
                "1px solid #e5e9ef",
              background:
                "#fafbfc",
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              gap: 12,
              flexWrap:
                "wrap",
              color:
                "#6b778c",
              fontSize: 13,
            }}
          >
            <span>
              Mostrando{" "}
              <strong>
                {
                  manteFiltrada.length
                }
              </strong>{" "}
              de{" "}
              <strong>
                {total}
              </strong>{" "}
              mantenciones
            </span>

            <span>
              Estado administrado
              por el proceso de
              alertas de
              mantención
            </span>
          </div>
        </Segment>
      </Container>
    </div>
  );
};

export default ListadoMant;